import { CanAccess } from '@modules/common/components/CanAccess';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  MAX_IMPORTANT_METADATA_COUNT,
  STAR_METADATA_MAX_LIMIT_MESSAGE,
  STAR_METADATA_VISIBILITY_OFF_MESSAGE,
} from '@modules/integrationsV2/constants';
import { useFetchMetadataDetailsForEqlSource } from '@modules/integrationsV2/hooks';
import { useUpdateMetadata } from '@modules/integrationsV2/hooks/useUpdateMetadata';
import { ManageMetadataTableRow } from '@modules/integrationsV2/types';
import { StarBorderRounded, StarRounded } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useEffect, useState } from 'react';

interface ManageMetadataStarIconButtonProps {
  metadata: ManageMetadataTableRow;
  eqlSource: string;
}

export const ManageMetadataStarIconButton = ({
  metadata,
  eqlSource,
}: ManageMetadataStarIconButtonProps) => {
  const { showErrorNotification } = useNotificationContext();

  const [isStarred, setIsStarred] = useState(metadata.isImportant);

  const [updateMetadata, { isLoading }] = useUpdateMetadata({
    onError: () => {
      showErrorNotification({
        contentComponent: `Failed to ${
          isStarred ? 'star' : 'unstar'
        } the metadata: "${metadata.displayText}"`,
        props: {},
      });
      setIsStarred((prev) => !prev);
    },
  });

  const { metadataDetails } = useFetchMetadataDetailsForEqlSource({
    eqlSource,
    fetchPolicy: 'cache-first', // We can use cache-first here, because we are fetching with cache-and-network in the root components
  });

  useEffect(() => {
    setIsStarred(metadata.isImportant);
  }, [metadata.isImportant]);

  const numberOfStaredMetadataFields = !isStarred
    ? metadataDetails?.filter(
        (m) =>
          m.source === metadata.source &&
          m.isImportant &&
          m.namespace === metadata.namespace,
      ).length ?? 0
    : 0;

  const hasReachedMaxStarsLimit =
    numberOfStaredMetadataFields >= MAX_IMPORTANT_METADATA_COUNT;

  const isHidden = metadata.isHidden;

  const getTooltipTitle = () => {
    if (isLoading) {
      return null;
    }

    if (hasReachedMaxStarsLimit) {
      return STAR_METADATA_MAX_LIMIT_MESSAGE;
    }

    if (isHidden) {
      return STAR_METADATA_VISIBILITY_OFF_MESSAGE;
    }

    return `${
      isStarred ? 'Unstar' : 'Star'
    } this field. Starred fields are shown upfront on feedback records.`;
  };

  const starOrUnstarMetadata = async () => {
    setIsStarred((prev) => !prev);

    updateMetadata({
      key: metadata.key,
      source: metadata.source,
      namespace: metadata.namespace,
      isImportant: !isStarred,
    });
  };

  const isDisabled = isLoading || isHidden || hasReachedMaxStarsLimit;

  return (
    <CanAccess
      resource={PermissionResource.Metadata}
      operation={PermissionOperation.Write}
      tooltipPlacement="top"
    >
      <Tooltip
        arrow
        title={getTooltipTitle()}
        placement="top"
        disableInteractive
      >
        <Box
          component="span"
          onClick={(e) => {
            // Prevents opening of edit metadata drawer, had to add to parent & not buttton itself,
            // because when button is disabled, it doesn't prevent the event from propagating
            e.stopPropagation();
          }}
          sx={{
            cursor: 'default', // Prevents the cursor from changing to pointer when button is disabled
          }}
        >
          <IconButton
            color="primary"
            onClick={starOrUnstarMetadata}
            disabled={isDisabled}
          >
            {isStarred ? <StarRounded /> : <StarBorderRounded />}
          </IconButton>
        </Box>
      </Tooltip>
    </CanAccess>
  );
};
